import type { RoomTypeTotalRange } from '../../lib/api.js';
import { fmtInt, fmtPct } from '../../lib/format.js';
import { CUENTAS_SERIES } from './ChartCard.js';

/** Un solo tono para toda la serie: la magnitud la codifica el largo, no el color. */
const BARRA = CUENTAS_SERIES[0]!;
/** El aforo de fondo, en el gris de superficie de la tarjeta. */
const AFORO = '#334155';

/**
 * Noches vendidas por tipo de habitación, con su aforo detrás.
 *
 * Barras horizontales: los nombres son códigos cortos pero la descripción es
 * larga, y en horizontal caben las dos. Una sola serie, así que no lleva
 * leyenda — el título la nombra — y cada barra va rotulada, que es además la
 * lectura accesible.
 *
 * La barra tenue de detrás son las **habitación-noches disponibles** de ese
 * tipo: sin ella, SGD (19 habitaciones) y SWP (3) se comparan como si compitieran
 * por lo mismo. Con ella se ve de un vistazo cuánto le queda a cada tipo.
 */
export function RoomTypeBars({ rows }: { rows: RoomTypeTotalRange[] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-slate-500">Sin tipos de habitación en el rango.</p>;
  }
  const max = Math.max(...rows.map((r) => r.disponibles), 1);

  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.roomType}>
          <div className="flex items-baseline justify-between gap-3 text-xs">
            <span className="min-w-0 truncate">
              <span className="font-medium text-slate-200">{r.roomType}</span>
              {r.descripcion && <span className="ml-2 text-slate-500">{r.descripcion}</span>}
            </span>
            <span className="shrink-0 tabular-nums text-slate-400">
              <span className="font-medium text-slate-100">{fmtInt(r.noches)}</span>
              <span className="text-slate-500"> / {fmtInt(r.disponibles)}</span>
              {r.occPct != null && (
                <span className="ml-1 text-slate-400">{fmtPct(r.occPct * 100)}</span>
              )}
            </span>
          </div>
          <div
            className="mt-1 h-3 rounded-sm"
            style={{ width: `${(r.disponibles / max) * 100}%`, backgroundColor: AFORO }}
          >
            <div
              className="h-3 rounded-sm"
              style={{
                width: r.disponibles > 0 ? `${(r.noches / r.disponibles) * 100}%` : '0%',
                backgroundColor: BARRA,
              }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}
